/**
 * DateTime Cache Module for Frontend
 * 
 * Provides in-memory caching for date/time conversions and timezone detection
 * to optimize performance of date/time operations in the frontend.
 */

/**
 * TTL Cache implementation for frontend
 */
class TTLCache {
  constructor(maxSize = 1000, ttl = 600000) { // Default 10 minutes TTL
    this.maxSize = maxSize;
    this.ttl = ttl; // TTL in milliseconds
    this.cache = new Map();
    this.timestamps = new Map();
  }

  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const timestamp = this.timestamps.get(key);
    const now = Date.now();

    // Check if expired
    if (now - timestamp > this.ttl) {
      this.cache.delete(key);
      this.timestamps.delete(key);
      return null;
    }

    return entry;
  }

  set(key, value) {
    // Remove oldest if at max size
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
      this.timestamps.delete(firstKey);
    }

    this.cache.set(key, value);
    this.timestamps.set(key, Date.now());
  }

  clear() {
    this.cache.clear();
    this.timestamps.clear();
  }

  size() {
    return this.cache.size;
  }

  stats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      ttl: this.ttl
    };
  }
}

/**
 * DateTimeCache - Centralized cache for DateTime operations
 */
class DateTimeCache {
  constructor() {
    // Conversion cache (UTC <-> user timezone)
    // Max 1000 entries, 10 minute TTL
    this.conversionCache = new TTLCache(1000, 600000);

    // Format cache (formatted strings)
    // Max 500 entries, 5 minute TTL
    this.formatCache = new TTLCache(500, 300000);

    // Timezone detection cache (permanent until browser refresh)
    this.timezoneCache = null;
    this.timezoneCacheTimestamp = null;

    // Statistics
    this.stats = {
      conversionHits: 0,
      conversionMisses: 0,
      formatHits: 0,
      formatMisses: 0,
      timezoneHits: 0,
      timezoneMisses: 0
    };

    // Auto-cleanup interval (every 10 minutes)
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 600000);

    // Initialize timezone cache
    this._initializeTimezoneCache();
  }

  /**
   * Initialize timezone detection cache
   */
  _initializeTimezoneCache() {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      this.timezoneCache = timezone;
      this.timezoneCacheTimestamp = Date.now();
      this.stats.timezoneHits = 1; // Count initial detection as hit
    } catch (error) {
      console.warn('Error initializing timezone cache:', error);
      this.timezoneCache = 'UTC';
      this.stats.timezoneMisses = 1;
    }
  }

  /**
   * Detect timezone (internal method)
   */
  _detectTimezone() {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      this.timezoneCache = timezone;
      this.timezoneCacheTimestamp = Date.now();
      return timezone;
    } catch (error) {
      console.warn('Error detecting timezone:', error);
      this.timezoneCache = 'UTC';
      return 'UTC';
    }
  }

  /**
   * Get cached timezone
   */
  getTimezone() {
    if (this.timezoneCache) {
      this.stats.timezoneHits++;
      return this.timezoneCache;
    }

    // Not cached, detect and cache
    this.stats.timezoneMisses++;
    return this._detectTimezone();
  }

  /**
   * Get cached conversion
   */
  getConversion(utcTimestamp, targetTimezone) {
    const key = `${utcTimestamp}_${targetTimezone}`;
    const cached = this.conversionCache.get(key);

    if (cached !== null) {
      this.stats.conversionHits++;
      return cached;
    }

    this.stats.conversionMisses++;
    return null;
  }

  /**
   * Cache conversion
   */
  setConversion(utcTimestamp, targetTimezone, convertedTimestamp) {
    const key = `${utcTimestamp}_${targetTimezone}`;
    this.conversionCache.set(key, convertedTimestamp);
  }

  /**
   * Get cached format
   */
  getFormat(timestamp, formatPreset, timezone) {
    const key = `${timestamp}_${formatPreset}_${timezone}`;
    const cached = this.formatCache.get(key);

    if (cached !== null) {
      this.stats.formatHits++;
      return cached;
    }

    this.stats.formatMisses++;
    return null;
  }

  /**
   * Cache format
   */
  setFormat(timestamp, formatPreset, timezone, formattedString) {
    const key = `${timestamp}_${formatPreset}_${timezone}`;
    this.formatCache.set(key, formattedString);
  }

  /**
   * Clear all caches
   */
  clearAll() {
    this.conversionCache.clear();
    this.formatCache.clear();
    this.timezoneCache = null;
    this.timezoneCacheTimestamp = null;
    this.stats = {
      conversionHits: 0,
      conversionMisses: 0,
      formatHits: 0,
      formatMisses: 0,
      timezoneHits: 0,
      timezoneMisses: 0
    };
    this._initializeTimezoneCache();
  }

  /**
   * Cleanup expired entries
   */
  cleanup() {
    // TTL caches handle their own cleanup, but we can force cleanup here
    // The cleanup happens automatically via TTL checks
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const conversionTotal = this.stats.conversionHits + this.stats.conversionMisses;
    const formatTotal = this.stats.formatHits + this.stats.formatMisses;
    const timezoneTotal = this.stats.timezoneHits + this.stats.timezoneMisses;

    return {
      conversion: {
        hits: this.stats.conversionHits,
        misses: this.stats.conversionMisses,
        hitRate: conversionTotal > 0 ? this.stats.conversionHits / conversionTotal : 0,
        cacheSize: this.conversionCache.size(),
        ...this.conversionCache.stats()
      },
      format: {
        hits: this.stats.formatHits,
        misses: this.stats.formatMisses,
        hitRate: formatTotal > 0 ? this.stats.formatHits / formatTotal : 0,
        cacheSize: this.formatCache.size(),
        ...this.formatCache.stats()
      },
      timezone: {
        hits: this.stats.timezoneHits,
        misses: this.stats.timezoneMisses,
        hitRate: timezoneTotal > 0 ? this.stats.timezoneHits / timezoneTotal : 0,
        cached: this.timezoneCache !== null
      }
    };
  }

  /**
   * Destroy cache instance (cleanup intervals)
   */
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.clearAll();
  }
}

// Global cache instance (singleton)
let cacheInstance = null;

/**
 * Get global cache instance
 */
export function getCache() {
  if (!cacheInstance) {
    cacheInstance = new DateTimeCache();
  }
  return cacheInstance;
}

/**
 * Clear global cache
 */
export function clearCache() {
  if (cacheInstance) {
    cacheInstance.clearAll();
  }
}

/**
 * Get cache statistics
 */
export function getCacheStats() {
  if (!cacheInstance) {
    return null;
  }
  return cacheInstance.getStats();
}

export default DateTimeCache;
